 import React, {
  useLayoutEffect,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import useFetch from "../../useFetch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faList,
  faSearch,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { getCokkie } from "../../../views/login/Login";
import { initLogsWorker, terminateLogsWorker } from "../../../worker/logsWorkerManger";
import { CalendarIcon, CodeIcon } from "../../../views/Common/SvgIcons/WorkflowIcons";
import "../../../views/project/models/Models.scss";
import { StyledTooltip } from "../../../views/project/label/annotation/EditorView/EditorTopNavigationBar/EditorTopNavigationBar";

function parseExperimentLogsUrl(evalUrl = "") {
  const match = String(evalUrl).match(/^experiments\/([^/]+)\/(stream_logs|logs)$/);
  if (!match) return null;
  return { experimentId: match[1], mode: match[2] };
}

function normalizeLogStatus(message, status) {
  return String(message || status || "").toLowerCase();
}

function isTerminalLogStatus(statusValue) {
  return ["complete", "completed", "terminated", "paused", "stopped", "failed"].includes(
    statusValue
  );
}

function normalizeLogsPayload(logs) {
  if (Array.isArray(logs)) return logs;
  if (typeof logs === "string") return logs.split("\n");
  return [];
}


export default function Logs(props) {
  const { cache, setCache, live } = props;

  const [data, setData] = useState({
    logs: [],
    line_count: 0,
    status: "pending",
  });

  // const [logdata, setLogdata] = useState('')
  const [logdata, setLogdata] = useState({ logs: "", status: "pending" });

  const [pause, setPause] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [filteredLogs, setFilteredLogs] = useState("");
  const [showTime, setShowTime] = useState(false);
  const [showTimeCont, setShowTimeCont] = useState(false);

  const containerRef = useRef(null);
  const workerUrlRef = useRef(null);
  const prevLogsRef = useRef("");
  const dropdownRef = useRef(null);

  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalPages, setTotalPages] = useState(null);
  const [completedLogs, setCompletedLogs] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [evaluationLogMode, setEvaluationLogMode] = useState(() =>
    isTerminalLogStatus(normalizeLogStatus(props.message, props.status))
      ? "static"
      : "stream"
  );
  const prevScrollHeightRef = useRef(0);
  const TAIL_MAX_LINES = 50;

  // "tail" | "history"
  // const [runHistoryPage, setRunHistoryPage] = useState(1);
  const latestPageRef = useRef(0);      // from backend stream
  const [currentPage, setCurrentPage] = useState(null);
  const [runViewMode, setRunViewMode] = useState("tail");
  const latestPageWorkerRef = useRef(null);
  const experimentLatestPageRequestRef = useRef(0);

  const baseEvalUrl = props.EvalUrl || "";
  const experimentLogsMeta = parseExperimentLogsUrl(baseEvalUrl);
  const experimentId = experimentLogsMeta?.experimentId ?? props.eval_id;
  const isExperimentLogs = Boolean(experimentLogsMeta);
  const isStreamingExperiment = experimentLogsMeta?.mode === "stream_logs";
  const normalizedStatus = normalizeLogStatus(props.message, props.status);
  const isTerminalExperiment = isTerminalLogStatus(normalizedStatus);
  const isRunningExperimentStream = isStreamingExperiment && !isTerminalExperiment;
  const isCompletedExperiment = isExperimentLogs && !isRunningExperimentStream;
  const supportsExperimentPagination = isRunningExperimentStream;

  // Evaluation logs expose section=logs/latest_log_page/paginated_logs.
  const supportsPaginatedLogs = baseEvalUrl.includes("section=logs");
  const latestPageUrl = props.EvalUrl && supportsPaginatedLogs
    ? `${window.ENV.API_URL}/${props.EvalUrl.replace(
      "section=logs",
      "section=latest_log_page"
    )}`
    : null;


  const autoScrollRef = useRef(true); // true means user is near bottom
  const BOTTOM_THRESHOLD = 80; // px

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      autoScrollRef.current = distanceToBottom < BOTTOM_THRESHOLD;
    };

    el.addEventListener("scroll", onScroll);
    onScroll(); // initialize

    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // -----------------------
  // Build URL
  // -----------------------
  const url = props.EvalUrl
    ? `${window.ENV.API_URL}/${props.EvalUrl}`
    : `${props.type}/detailed/${props.id}/logs${props.query
      ? props.query + `&line_count=${data.line_count}`
      : `?line_count=${data.line_count}`
    }`;

  const isCompletedEval =
    Boolean(props.EvalUrl) &&
    supportsPaginatedLogs &&
    evaluationLogMode === "static";

  const isCompletedLogView = isCompletedEval || isCompletedExperiment;

  const completedLogsUrl =
    isCompletedEval || isCompletedExperiment
      ? isCompletedExperiment
        ? `${window.ENV.API_URL}/experiments?action=logs&filter=${encodeURIComponent(JSON.stringify({ exp_id: experimentId }))}&page=${page}&page_size=${pageSize}`
        : `${window.ENV.API_URL}/${props.EvalUrl}&page=${page}&page_size=${pageSize}`
      : null;

  const isRunningEval =
    !!props.EvalUrl &&
    supportsPaginatedLogs &&
    evaluationLogMode === "stream" &&
    !isTerminalLogStatus(normalizedStatus);

  const experimentHistoryUrl =
    isRunningExperimentStream && runViewMode === "history" && currentPage
      ? `${window.ENV.API_URL}/experiments?action=logs&filter=${encodeURIComponent(JSON.stringify({ exp_id: experimentId }))}&page=${currentPage}&page_size=${pageSize}`
      : null;

  const experimentStreamUrl = isRunningExperimentStream
    ? `${window.ENV.API_URL}/${baseEvalUrl}`
    : null;

  // Tail URL must use section=logs
  const runningTailUrl = props.EvalUrl
    ? `${window.ENV.API_URL}/${baseEvalUrl.replace("section=paginated_logs", "section=logs")}`
    : null;

  // History URL must use section=paginated_logs + page params
  const runningHistoryUrl = props.EvalUrl && supportsPaginatedLogs
    ? `${window.ENV.API_URL}/${baseEvalUrl.replace("section=logs", "section=paginated_logs")}&page=${currentPage}`
    : null;
  // -----------------------
  // Polling (no EvalUrl)
  // -----------------------
  const { data: result, error, refetch } = useFetch(!props.EvalUrl ? url : null);

  // useEffect(() => {
  //   if (!cache) return;
  //   setData(cache);
  // }, [props.EvalUrl]);

  // useEffect(() => {
  //   if (!props.EvalUrl && result) {
  //     if (result.logs !== ".") {
  //       setData((prev) => ({
  //         logs: (prev.logs || "") + (result.logs || ""),
  //         line_count:
  //           result.status === "completed"
  //             ? prev.line_count
  //             : result.line_count,
  //         status: result.status,
  //       }));
  //       !pause && setPause(true);
  //     }

  //     if (result.status !== "completed") {
  //       const timer = setTimeout(() => refetch(), 5000);
  //       return () => clearTimeout(timer);
  //     }
  //   }
  // }, [result]);

  // if (error) console.error(error);

  // -----------------------
  // SSE Worker (EvalUrl)
  // -----------------------
  // useEffect(() => {
  //   if (!props.EvalUrl || url === workerUrlRef.current) return;

  //   const token = getCokkie("jwtToken");
  //   initLogsWorker(
  //     url,
  //     token,
  //     (logData) => {
  //       setData({
  //         logs: logData.logs,   // <-- replace
  //         status: logData.status
  //       });

  //       if (!pause) {
  //         containerRef.current?.scrollTo({
  //           top: containerRef.current.scrollHeight,
  //           behavior: "smooth",
  //         });
  //       }
  //     },
  //     (err) => console.error("❌ Worker Error:", err)
  //   );


  //   workerUrlRef.current = url;
  // }, [props.EvalUrl, url, pause, props.message]);

  useEffect(() => {
    if (!isRunningEval && !isRunningExperimentStream) return;
    setRunViewMode("tail");
    setCurrentPage(null);
    latestPageRef.current = 0;
  }, [props.EvalUrl, isRunningEval, isRunningExperimentStream]);

  const fetchExperimentLatestPage = useCallback(async () => {
    if (!experimentId) return 0;

    const requestId = ++experimentLatestPageRequestRef.current;
    const token = getCokkie("jwtToken");

    try {
      const res = await fetch(
        `${window.ENV.API_URL}/experiments?action=logs&filter=${encodeURIComponent(JSON.stringify({ exp_id: experimentId }))}&page=1&page_size=${pageSize}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) return latestPageRef.current || 0;

      const json = await res.json();
      if (requestId !== experimentLatestPageRequestRef.current) {
        return latestPageRef.current || 0;
      }

      const totalPagesFromApi = Number(json?.total_pages);
      const latestPage = Number.isFinite(totalPagesFromApi) && totalPagesFromApi > 0
        ? totalPagesFromApi
        : 1;

      latestPageRef.current = latestPage;
      return latestPage;
    } catch (err) {
      console.error("Failed to fetch experiment latest log page", err);
      return latestPageRef.current || 0;
    }
  }, [experimentId, pageSize]);


  useEffect(() => {
    if (!isCompletedLogView) return;
    if (isFetching) return;

    const fetchCompletedLogs = async () => {
      try {
        setIsFetching(true);
        const token = getCokkie("jwtToken");

        const res = await fetch(completedLogsUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json();

        const safeLogs = normalizeLogsPayload(json?.logs);

        if (page === 1) {
          setTotalPages(json?.total_pages ?? 0);
          setCompletedLogs(safeLogs);
        } else {
          setCompletedLogs((prev) => [...safeLogs, ...(Array.isArray(prev) ? prev : [])]);
        }
      } catch (err) {
        console.error("❌ Failed to fetch completed logs", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchCompletedLogs();
  }, [page, completedLogsUrl, isCompletedLogView]);




  useEffect(() => {
    if (!isRunningExperimentStream) return;
    if (runViewMode !== "history") return;
    if (!experimentHistoryUrl) return;

    const fetchExperimentHistoryLogs = async () => {
      try {
        setIsFetching(true);
        const token = getCokkie("jwtToken");

        const res = await fetch(experimentHistoryUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json();
        const safeLogs = normalizeLogsPayload(json?.logs);

        if (Number.isFinite(Number(json?.total_pages)) && Number(json.total_pages) > 0) {
          latestPageRef.current = Number(json.total_pages);
        }

        setLogdata({
          logs: safeLogs.join("\n"),
          status: "history",
        });
      } catch (err) {
        console.error("Failed to fetch experiment history logs", err);
      } finally {
        setIsFetching(false);
      }
    };

    fetchExperimentHistoryLogs();
  }, [experimentHistoryUrl, isRunningExperimentStream, runViewMode]);




  useEffect(() => {
    return () => {
      terminateLogsWorker();
    };
  }, []);

  useEffect(() => {
    if (!supportsPaginatedLogs) return;

    if (normalizedStatus === "evaluating" && evaluationLogMode === "static") {
      workerUrlRef.current = null;
      setEvaluationLogMode("stream");
    }
  }, [evaluationLogMode, normalizedStatus, supportsPaginatedLogs]);

  useEffect(() => {
    if (isRunningEval) return;
    terminateLogsWorker();
    workerUrlRef.current = null;
  }, [isRunningEval]);




  useEffect(() => {
    if (!isRunningExperimentStream) return;
    if (runViewMode !== "tail") {
      terminateLogsWorker();
      workerUrlRef.current = null;
      return;
    }
    if ((cache && !live)) return;

    if (!experimentStreamUrl) return;
    if (experimentStreamUrl === workerUrlRef.current) return;

    const token = getCokkie("jwtToken");

    initLogsWorker(
      experimentStreamUrl,
      token,
      (logData) => {
        setLogdata(logData);

        if (autoScrollRef.current) {
          containerRef.current?.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      },
      (err) => console.error("Experiment stream logs error:", err),
      { mode: "append_tail", maxLines: TAIL_MAX_LINES }
    );

    workerUrlRef.current = experimentStreamUrl;
  }, [
    isRunningExperimentStream,
    runViewMode,
    experimentStreamUrl,
    cache,
    live,
  ]);




  // useEffect(() => {
  //     console.log("STATE UPDATED:", logdata);
  //     setData(prev => ({
  //       ...prev,
  //       logs: logdata.logs,
  //       status: logdata.status,
  //     }));
  //   },[logdata.logs])


  // useEffect(() => {
  //   if(props.message === "Complete" || props.message === "Terminated") return;
  //   if (!props.EvalUrl || url === workerUrlRef.current) return;
  //   if ((cache && !live) || !props.EvalUrl || url === workerUrlRef.current) return;


  //     const token = getCokkie("jwtToken");
  //     initLogsWorker(
  //       url,
  //       token,
  //       (logData) => {
  //         // console.log('logData', logData)
  //         setLogdata(logData);


  //         // if (!pause) {
  //         //   containerRef.current?.scrollTo({
  //         //     top: containerRef.current.scrollHeight,
  //         //     behavior: "smooth",
  //         //   });
  //         // }
  //         if (autoScrollRef.current) {
  //           containerRef.current?.scrollTo({
  //             top: containerRef.current.scrollHeight,
  //             behavior: "smooth",
  //           });
  //         }

  //       },
  //       (err) => console.error("❌ Worker Error:", err)
  //     );


  //   workerUrlRef.current = url;
  // }, [props.EvalUrl, url, pause, props.message]);

  useEffect(() => {
    if (!props?.eval_id) return;
    if (!isRunningEval) return;
    if ((cache && !live)) return;
    if (props.message === "Complete" || props.message === "Terminated") return;

    const token = getCokkie("jwtToken");

    const effectiveUrl = runViewMode === "tail" ? runningTailUrl : runningHistoryUrl;
    if (!effectiveUrl) return;

    // prevent duplicate init
    if (effectiveUrl === workerUrlRef.current) return;

    initLogsWorker(
      effectiveUrl,
      token,
      (logData) => {
        setLogdata(logData);

        // Auto-scroll only in tail mode AND only if user is near bottom
        if (runViewMode === "tail" && autoScrollRef.current) {
          containerRef.current?.scrollTo({
            top: containerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      },
      (err) => console.error("❌ Worker Error:", err),
      runViewMode === "tail"
        ? { mode: "append_tail", maxLines: TAIL_MAX_LINES }
        : { mode: "replace" }
    );

    workerUrlRef.current = effectiveUrl;
  }, [
    isRunningEval,
    runViewMode,
    runningTailUrl,
    runningHistoryUrl,
    cache,
    live,
    props.eval_id,
    props.message,
  ]);

  // useEffect(() => {
  //   if (!isRunningEval) return;
  //   if (!latestPageUrl) return;

  //   const token = getCokkie("jwtToken");

  //   const workerPath = new URL(
  //     "../../../worker/latestPageWorker.js",
  //     import.meta.url
  //   );

  //   const worker = new Worker(workerPath, { type: "module" });

  //   worker.postMessage({ url: latestPageUrl, token });

  //   worker.onmessage = (event) => {
  //     const msg = event.data;

  //     if (msg.type === "count") {
  //       // Always keep highest page number
  //       if (msg.count > latestPageRef.current) {
  //         latestPageRef.current = msg.count;
  //       }
  //     }

  //     if (msg.type === "error") {
  //       console.error("Latest Page Worker Error:", msg.message);
  //     }
  //   };

  //   return () => {
  //     worker.terminate();
  //   };

  // }, [isRunningEval, latestPageUrl]);
  useEffect(() => {
    // ❌ Do not run if:
    // - not running eval
    // - no URL
    // - status is "Starting"
    if (!isRunningEval) return;
    if (!latestPageUrl) return;
    // if (props.message === "Starting") return;

    const token = getCokkie("jwtToken");
    if (!token) return;

    // ✅ Kill previous worker if exists
    if (latestPageWorkerRef.current) {
      latestPageWorkerRef.current.terminate();
      latestPageWorkerRef.current = null;
    }

    const workerPath = new URL(
      "../../../worker/latestPageWorker.js",
      import.meta.url
    );

    const worker = new Worker(workerPath, { type: "module" });
    latestPageWorkerRef.current = worker;

    worker.postMessage({ url: latestPageUrl, token });

    worker.onmessage = (event) => {
      const msg = event.data;

      if (msg.type === "count") {
        if (msg.count > latestPageRef.current) {
          latestPageRef.current = msg.count;
        }
      }

      if (msg.type === "error") {
        console.error("Latest Page Worker Error:", msg.message);
      }

      if (msg.type === "complete") {
        worker.terminate();
        latestPageWorkerRef.current = null;
      }
    };

    return () => {
      if (latestPageWorkerRef.current) {
        latestPageWorkerRef.current.terminate();
        latestPageWorkerRef.current = null;
      }
    };

  }, [isRunningEval, latestPageUrl, props.message]);


  useEffect(() => {
    if (!isRunningEval && !supportsExperimentPagination) return;

    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {

      // ---------------------------
      // 🔼 SCROLL UP (Top)
      // ---------------------------
      if (container.scrollTop === 0) {

        if (runViewMode === "tail") {

          if (supportsExperimentPagination) {
            fetchExperimentLatestPage().then((latestPage) => {
              if (latestPage > 0) {
                terminateLogsWorker();
                workerUrlRef.current = null;
                setRunViewMode("history");
                setCurrentPage(latestPage);
              }
            });
            return;
          }

          if (latestPageRef.current > 0) {
            setRunViewMode("history");
            setCurrentPage(latestPageRef.current);
          }

          return;
        }

        if (runViewMode === "history" && currentPage > 1) {
          prevScrollHeightRef.current = container.scrollHeight;
          setCurrentPage((prev) => prev - 1);
        }

        return;
      }

      // ---------------------------
      // 🔽 SCROLL DOWN (Bottom)
      // ---------------------------
      const distanceToBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;

      if (distanceToBottom < 5) {

        if (runViewMode === "history") {

          if (currentPage < latestPageRef.current) {
            setCurrentPage((prev) => prev + 1);
          } else {
            terminateLogsWorker();
            workerUrlRef.current = null;
            setRunViewMode("tail");
            setCurrentPage(null);
          }
        }
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);

  }, [
    isRunningEval,
    supportsExperimentPagination,
    runViewMode,
    currentPage,
    supportsPaginatedLogs,
    fetchExperimentLatestPage,
  ]);

  // -----------------------
  // Scroll to bottom
  // -----------------------
  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  useLayoutEffect(() => {
    if (!isCompletedLogView) return;
    if (page !== 1) return;

    const container = containerRef.current;
    if (!container) return;

    // 👇 force scroll to bottom on first page
    container.scrollTop = container.scrollHeight;
  }, [completedLogs, isCompletedLogView, page]);


  // useLayoutEffect(() => {
  //   if (autoScrollRef.current && data.logs !== prevLogsRef.current) {
  //     scrollToBottom();
  //     prevLogsRef.current = data.logs;
  //   }
  // }, [data.logs, pause]);

  //   useLayoutEffect(() => {
  //   const sourceLogs = props.EvalUrl ? logdata.logs : data.logs;

  //   if (autoScrollRef.current && sourceLogs !== prevLogsRef.current) {
  //     scrollToBottom();
  //     prevLogsRef.current = sourceLogs;
  //   }
  // }, [logdata.logs, data.logs, pause, props.EvalUrl]);
  useLayoutEffect(() => {
    const sourceLogs = props.EvalUrl ? logdata.logs : data.logs;

    // 🚀 ONLY auto-scroll in tail mode
    if (
      runViewMode === "tail" &&
      autoScrollRef.current &&
      sourceLogs !== prevLogsRef.current
    ) {
      scrollToBottom();
      prevLogsRef.current = sourceLogs;
    }
  }, [logdata.logs, data.logs, pause, props.EvalUrl, runViewMode]);

  useEffect(() => {
    if (props.selectedAction === "Logs" && !pause) {
      scrollToBottom();
    }
  }, [props.selectedAction, pause]);

  useEffect(() => {
    if (autoScrollRef.current) scrollToBottom();
  }, [filteredLogs]);


  // -----------------------
  // Filtering by datetime
  // -----------------------
  // useEffect(() => {
  //   if (!data.logs) {
  //     setFilteredLogs(null);  
  //     return;
  //   }

  //   if (!startDateTime && !endDateTime) {
  //     setFilteredLogs(data.logs);
  //     return;
  //   }

  //   const lines = data.logs.split("\n");
  //   const filtered = lines.filter((line) => {
  //     const dateTimeMatch = line.match(
  //       /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/
  //     );
  //     if (!dateTimeMatch) return false;

  //     const logISO = dateTimeMatch[1].replace(" ", "T");
  //     if (startDateTime && logISO < startDateTime) return false;
  //     if (endDateTime && logISO > endDateTime) return false;
  //     return true;
  //   });

  //   setFilteredLogs(filtered.join("\n"));
  // }, [data.logs, startDateTime, endDateTime]);

  useEffect(() => {
    const sourceLogs = isCompletedLogView
      ? (Array.isArray(completedLogs) ? completedLogs.join("\n") : "")
      : props.EvalUrl
        ? logdata.logs
        : data.logs;

    if (!sourceLogs) {
      setFilteredLogs(null);
      return;
    }

    if (!startDateTime && !endDateTime) {
      setFilteredLogs(null); // important: null, not sourceLogs
      return;
    }

    const lines = sourceLogs.split("\n");

    const filtered = lines.filter((line) => {
      const dateTimeMatch = line.match(
        /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/
      );

      if (!dateTimeMatch) return false;

      const logISO = dateTimeMatch[1].replace(" ", "T");

      if (startDateTime && logISO < startDateTime) return false;
      if (endDateTime && logISO > endDateTime) return false;

      return true;
    });

    setFilteredLogs(filtered.length ? filtered.join("\n") : null);

  }, [
    isCompletedLogView,
    completedLogs,
    props.EvalUrl,
    logdata.logs,
    data.logs,
    startDateTime,
    endDateTime
  ]);


  useLayoutEffect(() => {
    if (!isCompletedLogView || page === 1) return;

    const container = containerRef.current;
    if (!container) return;

    const prevHeight = prevScrollHeightRef.current;
    const newHeight = container.scrollHeight;

    // 👇 maintain the same visible line
    container.scrollTop = newHeight - prevHeight;
  }, [completedLogs, isCompletedLogView, page]);

  useLayoutEffect(() => {
    if (!isRunningExperimentStream || runViewMode !== "history") return;
    if (!currentPage || currentPage >= latestPageRef.current) return;

    const container = containerRef.current;
    if (!container) return;

    const prevHeight = prevScrollHeightRef.current;
    if (prevHeight <= 0) return;

    container.scrollTop = container.scrollHeight - prevHeight;
  }, [logdata.logs, currentPage, runViewMode, isRunningExperimentStream]);



  const handleSearchChange = (e) => setSearchQuery(e.target.value);

  const highlightText = (text) => {
    if (!searchQuery) return text;
    const regex = new RegExp(`(${searchQuery})`, "gi");
    return text.split(regex).map((part, idx) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <span
          key={idx}
          className="highlighted"
          style={{ backgroundColor: "blue", fontWeight: "bold" }}
        >
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleScrollToResult = (index) => {
    const elements =
      containerRef.current?.getElementsByClassName("highlighted") || [];
    if (elements[index]) {
      elements[index].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleNextResult = () =>
    setCurrentResultIndex(
      (prev) => (prev + 1) % (searchResults.length || 1)
    );

  const handlePrevResult = () =>
    setCurrentResultIndex(
      (prev) =>
        (prev - 1 + (searchResults.length || 1)) % (searchResults.length || 1)
    );

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.stopPropagation();
      handleNextResult();
    }
  };

  useEffect(() => {
    if (!showTime) return;

    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setShowTime(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTime]);


  // -----------------------
  // ✅ Improved formatLogs
  // -----------------------
  // const formatLogs = (logs) => {
  //   if (!logs) return null;

  //   if (Array.isArray(logs)) {
  //     if (logs.length === 1 && logs[0] === "[]") return null;
  //     logs = logs.join("\n");
  //   }

  //   if (typeof logs !== "string") logs = String(logs);

  //   if (logs.trim() === "[]") return null;

  //   try {
  //     const arr = JSON.parse(logs);
  //     if (Array.isArray(arr) && arr.length === 0) return null;
  //   } catch (_) {}

  //   return logs.trim();
  // };

  const formatLogs = (logs) => {
    if (!logs) return null;

    // Convert array → string
    if (Array.isArray(logs)) {
      if (logs.length === 1 && logs[0] === "[]") return null;
      logs = logs.join("\n");
    }

    if (typeof logs !== "string") logs = String(logs);
    if (logs.trim() === "[]") return null;

    const lines = logs.split("\n");
    const deduped = [];

    for (let i = 0; i < lines.length; i++) {
      if (i === 0 || lines[i] !== lines[i - 1]) {
        deduped.push(lines[i]);
      }
    }

    const result = deduped.join("\n").trim();
    return result.length ? result : null;
  };



  const AnimatedDots = () => {
    const [dots, setDots] = React.useState(1);

    React.useEffect(() => {
      const interval = setInterval(() => {
        setDots((prev) => (prev === 4 ? 1 : prev + 1));
      }, 500); // speed (ms)

      return () => clearInterval(interval);
    }, []);

    return ".".repeat(dots);
  };

  // const renderedLogs = isCompletedEval
  // ? completedLogs.join("\n")
  // : props.EvalUrl
  //   ? filteredLogs ?? logdata.logs
  //   : data.logs;

  const renderedLogs =
    filteredLogs ??
    (
      isCompletedLogView
        ? (Array.isArray(completedLogs) ? completedLogs.join("\n") : "")
        : props.EvalUrl
          ? logdata.logs
          : data.logs
    );



  // -----------------------
  // Search
  // -----------------------

  const searchableLogs = formatLogs(renderedLogs) || "";

  useEffect(() => {
    if (searchQuery && searchableLogs) {
      const results = [];
      const regex = new RegExp(`(${searchQuery})`, "gi");
      let match;

      while ((match = regex.exec(searchableLogs)) !== null) {
        results.push(match.index);
      }

      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, searchableLogs]);


  useEffect(() => {
    if (searchResults.length > 0) {
      handleScrollToResult(currentResultIndex);
    }
  }, [currentResultIndex, searchResults]);

  useEffect(() => {
    if (!isCompletedLogView) return;

    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (
        container.scrollTop === 0 &&
        !isFetching &&
        totalPages &&
        page < totalPages
      ) {
        // 👇 capture BEFORE page change
        prevScrollHeightRef.current = container.scrollHeight;

        setPage((prev) => prev + 1);
      }
    };


    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [page, totalPages, isFetching, isCompletedLogView]);


  return (
    <>
      <div className={`d-a justify-content-between flex-wrap mb-2 evaluation-logs-toolbar${props.hideTitle ? " evaluation-logs-toolbar--title-hidden" : ""}`}>
        {!props.hideTitle && (
          <h6 className="fw-bold text-white mb-0">  <CodeIcon
            className="wf-icon text-white"
          /> {isExperimentLogs ? "Training Logs" : "Evaluation Logs"}</h6>
        )}
        <div className="col-12 col-sm-auto evaluation-logs-search-form">
          <div className="d-a gap-2 flex-fill">

            {searchQuery.length !== 0 && <>
              <span className="" style={{ color: searchResults.length ? 'white' : "red" }}>
                {searchResults.length
                  ? `${currentResultIndex + 1} / ${searchResults.length}`
                  : "No Result"}
              </span>

              <button
                className="plain-button ms-1"
                onClick={handlePrevResult}
                disabled={searchResults.length === 0}
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </button>
              <button
                className="plain-button"
                onClick={handleNextResult}
                disabled={searchResults.length === 0}
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </button>
            </>}

            <div className="d-a container-card px-3 py-1 rounded prediction-search-input bg-transparent evaluation-logs-search-input">
              <FontAwesomeIcon
                className="plain-button2"
                icon={faSearch}
                style={{
                  position: "relative",
                  opacity: "0.8",
                  color: searchQuery.length !== 0 ? 'var(--text-color)' : 'gray'
                }}
              />
              <input
                type="text"
                className="border-0 outline-none text bg-transparent p-0 ps-3"
                style={{ outline: 'none' }}
                placeholder="Search logs..."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyPress}
              />
            </div>


            <div style={{ position: "relative" }}>
              <button
                className="plain-button p-0 m-0"
                onClick={() => setShowTime((prev) => !prev)}
              >
                <CalendarIcon
                  className="wf-icon text-primary"
                  style={{ marginBottom: "1px" }}
                />
              </button>

              {showTime && (
                <div
                  ref={dropdownRef}
                  className="date-dropdown"
                  onClick={(e) => e.stopPropagation()}
                >
                  {showTime && (
                    <div className="d-a justify-content-between gap-2 mt-3 rounded p-3" style={{ position: 'absolute', zIndex: '9', right: '10px', background: 'black', width: '400px', border: '1px solid #4646469e' }}>
                      <label className="d-flex flex-column flex-fill  text-white">
                        <span className="d-a justify-content-between  text-white">
                          Start Date & Time:
                          {startDateTime && (
                            <button
                              className="text-white"
                              type="button"
                              onClick={() => setStartDateTime("")}
                            >
                              ×
                            </button>
                          )}
                        </span>
                        <input
                          className="badge-border text-white px-2"
                          style={{
                            borderColor: '#4646469e',
                            colorScheme: "dark",   // 👈 THIS
                            color: "white",
                          }}
                          type="datetime-local"
                          value={startDateTime}
                          onChange={(e) => setStartDateTime(e.target.value)}
                        />
                      </label>

                      <label className="d-flex flex-column flex-fill text-white">
                        <span className="d-a justify-content-between text-white">
                          End Date & Time:
                          {endDateTime && (
                            <button
                              className="text-white"
                              type="button"
                              onClick={() => setEndDateTime("")}
                            >
                              ×
                            </button>
                          )}
                        </span>
                        <input
                          className="badge-border text-white px-2"
                          style={{
                            borderColor: '#4646469e',
                            colorScheme: "dark",   // 👈 THIS
                            color: "white",
                          }}
                          type="datetime-local"
                          value={endDateTime}
                          onChange={(e) => setEndDateTime(e.target.value)}
                        />
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>


        </div>
      </div>

      {/* Logs output */}
      <pre
        className="md-height-logs"
        ref={containerRef}
        id="logs"
        style={{
          backgroundColor: "#000",
          color: "white",
          overflowY: "auto",
          height: props?.full ? '95%' : 'calc(100vh - 500px)',
          padding: props.hub ? "2px" : "32px",
          maxWidth: "100% !important",
          wordWrap: "break-word !important",
          whiteSpace: "pre-wrap",
          position: "relative",
          marginBottom: "0px",
          scrollbarWidth: "thin",
          fontFamily: "monospace",
          fontSize: "0.85rem",
        }}
      >
        {/* {logdata.logs} */}
        {/* {(() => {
    const formatted = props.EvalUrl
      ? formatLogs(filteredLogs ?? logdata.logs)
      : formatLogs(data.logs ?? logdata.logs);

    if (!formatted) {
      return <AnimatedDots />;
    }

    return props.EvalUrl
      ? highlightText(formatted)
      : formatted;
  })()} */}
        {(() => {
          const formatted = formatLogs(renderedLogs);
          if (!formatted) return <AnimatedDots />;

          return highlightText(formatted);
        })()}

        {((isRunningEval || supportsExperimentPagination) && runViewMode === "history") && (
          <StyledTooltip title="View Current Logs">

            <button
              className="scroll-to-bottom-btn"

              onClick={() => {
                terminateLogsWorker();
                workerUrlRef.current = null;
                setRunViewMode("tail");
                setCurrentPage(null);
                setTimeout(() => scrollToBottom(), 0);
              }}
            >
              <FontAwesomeIcon icon={faArrowDown} />
            </button>
          </StyledTooltip>
        )}

      </pre>
    </>
  );
}
